// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { cn } from "@/lib/utils"

export interface OptionItem {
  value: string
  label: string
  sublabel?: string
}

interface OptionGroupProps {
  legend: string
  options: OptionItem[]
  value: string | null
  onChange: (value: string) => void
  columns?: 1 | 2 | 3
}

/** Single-select tile group (main.html's `.options[data-group="single"]`). */
export function OptionGroup({ legend, options, value, onChange, columns = 1 }: OptionGroupProps) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="sr-only">{legend}</legend>
      <div
        role="radiogroup"
        aria-label={legend}
        className={cn(
          "grid gap-2",
          columns === 3 && "sm:grid-cols-3",
          columns === 2 && "sm:grid-cols-2"
        )}
      >
        {options.map(opt => {
          const selected = value === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              className={cn(
                // Press feedback (owner reference video): the tap visibly
                // lands (scale dip) before the screen moves on.
                "flex min-h-12 items-center gap-3 rounded-bb-lg border px-4 py-3 text-left transition-all duration-150 active:scale-[0.97]",
                selected
                  ? "border-green bg-green/10 text-ink shadow-sm"
                  : "border-line bg-paper text-ink hover:border-green/60 hover:shadow-sm"
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full border transition-all duration-150",
                  selected ? "border-green bg-green" : "border-line bg-paper"
                )}
              >
                <span
                  className={cn(
                    "size-2 rounded-full bg-paper transition-transform duration-150",
                    selected ? "scale-100" : "scale-0"
                  )}
                />
              </span>
              <span className="min-w-0">
                <span className="block font-semibold">{opt.label}</span>
                {opt.sublabel && <span className="block text-xs text-ink-2">{opt.sublabel}</span>}
              </span>
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}
